import { ArrowLeft } from '@phosphor-icons/react';
import {
  getGetSubscriptionQueryKey,
  useChooseSubscription,
  useListPlans,
} from '@reelplicate/api-client/billing';
import type { PlanDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Badge,
  Button,
  Inline,
  SegmentedControl,
  SegmentedControlItem,
  Skeleton,
  Stack,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import { OnboardingHeader } from './onboarding-card';
import { PlanCard } from './plan-card';
import { ONBOARDING_STEPS, PAYMENT_PATH, stepPath } from './steps';

type Billing = 'monthly' | 'yearly';

/**
 * Figma "Onboarding — 5 · Plan" (127:940): no card - a centred header, the
 * Monthly / Yearly Billing toggle (Segmented Control, "Save 20%" badge), the
 * three pricing cards (1080 wide, 24 apart, Growth highlighted), then Back
 * and the checkout note. Plans come from GET /plans; choosing one saves it
 * as the workspace's subscription (yearly starts a trial), then opens the
 * payment form (5b).
 */
export function PlanStepPage() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState<Billing>('monthly');
  const queryClient = useQueryClient();
  const { data: plans } = useListPlans();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id;
  const choose = useChooseSubscription();

  const onChoose = async (plan: PlanDto) => {
    if (!workspaceId) return;
    try {
      const subscription = await choose.mutateAsync({
        id: workspaceId,
        data: {
          plan: plan.id,
          billingPeriod: billing === 'yearly' ? 'YEARLY' : 'MONTHLY',
        },
      });
      queryClient.setQueryData(getGetSubscriptionQueryKey(workspaceId), subscription);
    } catch {
      return; // The failure is already shown as a toast.
    }
    await navigate(PAYMENT_PATH);
  };

  return (
    <Stack gap={6} align="center" className="w-full max-w-[1080px]">
      <OnboardingHeader
        align="center"
        eyebrow={`Step 5 of ${ONBOARDING_STEPS.length}`}
        title="Choose your plan"
        subtitle={
          billing === 'yearly'
            ? 'Start with a 14-day free trial. Pay $0 today, cancel anytime.'
            : 'Pay monthly and cancel anytime, or go yearly for 20% off and a 14-day free trial.'
        }
      />
      <SegmentedControl
        aria-label="Billing period"
        value={billing}
        onValueChange={(v) => setBilling(v as Billing)}
      >
        <SegmentedControlItem value="monthly">Monthly</SegmentedControlItem>
        <SegmentedControlItem value="yearly">
          Yearly
          <Badge tone="brand" size="sm">
            Save 20%
          </Badge>
        </SegmentedControlItem>
      </SegmentedControl>
      <Stack gap={6} className="w-full laptop:flex-row laptop:items-stretch">
        {plans
          ? plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={{
                  id: plan.id.toLowerCase(),
                  name: plan.name,
                  description: plan.description,
                  features: plan.features,
                  highlighted: plan.highlighted,
                  price: `$${billing === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}`,
                  period: billing === 'monthly' ? '/ month' : '/ month, billed yearly',
                  trialDays:
                    billing === 'yearly' ? plan.yearlyTrialDays : plan.monthlyTrialDays,
                  cta:
                    plan.highlighted && billing === 'yearly'
                      ? `Start ${plan.name} trial`
                      : `Choose ${plan.name}`,
                }}
                onChoose={() => void onChoose(plan)}
                disabled={choose.isPending}
              />
            ))
          : [0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                shape="rectangle"
                shimmer
                className="h-[520px] flex-1 rounded-2xl"
              />
            ))}
      </Stack>
      <Inline gap={4} align="center" wrap>
        <Button asChild variant="ghost" size="lg" leadingIcon={ArrowLeft}>
          <RouterLink to={stepPath(4)}>Back</RouterLink>
        </Button>
        <p className="text-caption text-text-tertiary">
          Secure checkout · Cancel anytime · Prices in USD
        </p>
      </Inline>
    </Stack>
  );
}
